import { useDeferredValue, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { CompactTablePagination, EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { useCustomerSearch } from '../../hooks';
import type { CustomerSearchRequest, CustomerSummary } from '../../model/types';
import { CustomerStatusChip, ProfileStatusChip } from '../components/chips';
import { customerDisplayName, formatDateTime, humanizeCode } from '../components/format';

type StatusFilter = 'all' | 'ACTIVE' | 'REGISTRATION_REQUIRED' | 'BLOCKED' | 'DELETED' | 'ANONYMIZED';
type ProfileFilter = 'all' | 'COMPLETE' | 'PHONE_REQUIRED';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

function ContactCell({ customer }: { customer: CustomerSummary }) {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <PhoneRoundedIcon aria-hidden sx={{ fontSize: 15, color: 'text.secondary' }} />
        <Typography variant="body2">{customer.primaryPhone ?? '—'}</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <EmailRoundedIcon aria-hidden sx={{ fontSize: 15, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {customer.primaryEmail ?? '—'}
        </Typography>
      </Stack>
    </Stack>
  );
}

export function CustomersListPage() {
  const { t } = useTranslation('customers');
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [profileFilter, setProfileFilter] = useState<ProfileFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const deferredSearch = useDeferredValue(searchInput);

  const canView = hasPermission('customers.view');
  const canOpenSettings = hasPermission('settings.customers.view');

  const request = useMemo<CustomerSearchRequest>(() => {
    const criteria: CustomerSearchRequest['criteria'] = {};
    if (statusFilter === 'DELETED' || statusFilter === 'ANONYMIZED') {
      criteria.deleted = true;
      criteria.statuses = [statusFilter];
    } else if (statusFilter !== 'all') {
      criteria.statuses = [statusFilter];
    }
    if (profileFilter !== 'all') {
      criteria.profileStatuses = [profileFilter];
    }
    return {
      query: deferredSearch.trim() || undefined,
      criteria: Object.keys(criteria).length ? criteria : undefined,
      pagination: { page, size: rowsPerPage },
      sort: [{ property: 'updatedAt', direction: 'DESC' }],
    };
  }, [deferredSearch, profileFilter, statusFilter, page, rowsPerPage]);

  const search = useCustomerSearch(request, { enabled: canView });

  const resetFilters = () => {
    setSearchInput('');
    setStatusFilter('all');
    setProfileFilter('all');
    setPage(0);
  };

  const settingsAction = canOpenSettings ? (
    <Tooltip title={t('list.settingsTooltip')}>
      <IconButton
        component={RouterLink}
        to="/settings/customers"
        aria-label={t('list.settingsTooltip')}
        sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <SettingsRoundedIcon />
      </IconButton>
    </Tooltip>
  ) : null;

  if (!canView) {
    return (
      <PageContainer title={t('list.title')} actions={settingsAction}>
        <ErrorState
          title={t('list.accessRestricted.title')}
          message={t('list.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  const customers = search.data?.content ?? [];
  const total = search.data?.totalElements ?? 0;

  return (
    <PageContainer
      title={t('list.title')}
      subtitle={t('list.subtitle')}
      actions={settingsAction}
    >
      <Stack spacing={2.5} sx={{ minHeight: 0 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(0);
              }}
              placeholder={t('list.searchPlaceholder')}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="customers-status-filter">{t('list.statusLabel')}</InputLabel>
              <Select
                labelId="customers-status-filter"
                value={statusFilter}
                label={t('list.statusLabel')}
                onChange={(event) => {
                  setStatusFilter(event.target.value as StatusFilter);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('list.statusOptions.all')}</MenuItem>
                <MenuItem value="ACTIVE">{t('list.statusOptions.active')}</MenuItem>
                <MenuItem value="REGISTRATION_REQUIRED">{t('list.statusOptions.registrationRequired')}</MenuItem>
                <MenuItem value="BLOCKED">{t('list.statusOptions.blocked')}</MenuItem>
                <MenuItem value="DELETED">{t('list.statusOptions.deleted')}</MenuItem>
                <MenuItem value="ANONYMIZED">{t('list.statusOptions.anonymized')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="customers-profile-filter">{t('list.profileLabel')}</InputLabel>
              <Select
                labelId="customers-profile-filter"
                value={profileFilter}
                label={t('list.profileLabel')}
                onChange={(event) => {
                  setProfileFilter(event.target.value as ProfileFilter);
                  setPage(0);
                }}
              >
                <MenuItem value="all">{t('list.profileOptions.all')}</MenuItem>
                <MenuItem value="COMPLETE">{t('list.profileOptions.complete')}</MenuItem>
                <MenuItem value="PHONE_REQUIRED">{t('list.profileOptions.phoneRequired')}</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" color="inherit" onClick={resetFilters} startIcon={<ClearRoundedIcon />}>
              {t('common:action.reset')}
            </Button>
          </Stack>
        </Paper>

        {search.isLoading ? (
          <LoadingState label={t('list.loading')} />
        ) : search.isError ? (
          <ErrorState
            title={t('list.loadError.title')}
            message={search.error?.message ?? t('common:error.unknown')}
            onRetry={() => void search.refetch()}
          />
        ) : customers.length === 0 ? (
          <EmptyState
            icon={<GroupsRoundedIcon sx={{ fontSize: 48 }} />}
            title={t('list.empty.title')}
            description={t('list.empty.description')}
            action={
              <Button variant="outlined" onClick={resetFilters} startIcon={<ClearRoundedIcon />}>
                {t('list.empty.resetAction')}
              </Button>
            }
          />
        ) : (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('list.table.customer')}</TableCell>
                    <TableCell>{t('list.table.contacts')}</TableCell>
                    <TableCell>{t('list.table.status')}</TableCell>
                    <TableCell>{t('list.table.channel')}</TableCell>
                    <TableCell>{t('list.table.updated')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow
                      key={customer.customerId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/customers/${customer.customerId}`)}
                    >
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {customerDisplayName(customer)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                          {customer.customerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ContactCell customer={customer} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <CustomerStatusChip status={customer.status} />
                          <ProfileStatusChip status={customer.profileStatus} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {customer.registrationChannel ? humanizeCode(customer.registrationChannel) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(customer.updatedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ px: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
              <CompactTablePagination
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          </Paper>
        )}
      </Stack>
    </PageContainer>
  );
}
